import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import PhoneNumber from 'awesome-phonenumber';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { colors, Elevation } from '../utils';
import { logout as logoutAction } from '../lib/redux';
import { setUnavailable, setAvailable, destroyWorker, rejectReservation } from '../lib/taskrouter';
import isLoadingSelector from '../lib/redux/selectors/app/is-loading';
import Logo from '../assets/images/logo.svg';
import InstructionsIcon from '../assets/images/instructions-line.svg';
import UserMaleIcon from '../assets/images/user-male.svg';
import Chevron from '../assets/images/chevron.svg';
import EditIconImg from '../assets/images/edit-icon.svg';
import EditIconBlueImg from '../assets/images/edit-icon-blue.svg';
import { Typography } from './Typography';
import { GuidelinesText } from './GuidelinesText';
import { Alert } from './Alert';
import EditPhoneNumberModal from './EditPhoneNumberModal';
import { ROUTES } from '../Constants';
import Faq from './Faq';
import { getPhoneNumber } from '../lib/redux/selectors/me';
import { gotoDashboard as gotoDashboardAction } from '../lib/redux/actions/dashboard';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Navbar = styled.div`
  background-color: ${colors.w01};
  display: flex;
  flex-direction: row;
  height: 72px;
  left: 0;
  position: fixed;
  padding: 0;
  right: 0;
  top: 0;
  z-index: ${Elevation.Navbar};
`;

const NavbarAlert = styled(Alert)`
  position: absolute;
  top: 100%;
  left: 0;
  border-radius: 0;

  a {
    color: ${colors.w01};
  }
`;

const Col = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  justify-content: center;

  &:first-child {
    justify-content: flex-start;
  }

  &:nth-child(3) {
    justify-content: flex-end;
  }
`;

const LogoNav = styled.img`
  height: 40px;
  width: 40px;
`;

const Body = styled.div`
  align-items: center;
  background-color: ${colors.g01};
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-top: 72px;
`;

const InstructionsButton = styled.button.attrs({
  type: 'button'
})`
  align-items: center;
  background-color: transparent;
  border: 0 none;
  cursor: pointer;
  display: flex;
  height: 100%;
  justify-content: center;
  padding: 0 9px;

  &:focus {
    outline: none;
  }
`;

const InstructionsButtonIcon = styled.img`
  height: 40px;
  width: 40px;
`;

const InstructionsButtonText = styled.span`
  color: ${colors.g04};
  font-family: Mada;
  font-size: 16px;
  font-weight: bold;
`;

const UserDropdown = styled.div`
  align-items: center;
  background-color: transparent;
  border: 0 none;
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  height: 100%;
  padding: 0 16px 0 16px;

  &:focus {
    outline: none;
  }
`;

const UserIconWrapper = styled.div`
  position: relative;
`;

const UserIcon = styled.img`
  height: 41px;
  width: 40px;
`;

const EditIcon = styled.span`
  display: inline-block;
  background: url(${EditIconImg});
  height: 24px;
  width: 24px;
`;

const ChevronIcon = styled.img`
  height: 24px;
  width: 24px;
  transition: 0.2s ease;
  transform: rotate(${({ isMenuOpen }) => (isMenuOpen ? 270 : 90)}deg);
`;

const StatusIndicator = styled.div`
  background-color: ${props => (props.isAvailable ? colors.gr01 : colors.r01)};
  border-radius: 9px;
  bottom: ${props => (props.static ? 0 : 4)}px;
  height: 9px;
  position: ${props => (props.static ? 'relative' : 'absolute')};
  right: ${props => (props.static ? 'auto' : 0)};
  left: ${props => (props.static ? '4px' : 'auto')};
  width: 9px;
`;

const GuidelinesSidebar = styled.div`
  background-color: ${colors.w01};
  border: 1px solid ${colors.g02};
  bottom: 0;
  left: 0;
  overflow-y: auto;
  position: fixed;
  top: 72px;
  transform: translateX(-400px);
  transition: transform 0.3s;
  width: 400px;
  z-index: ${Elevation.Navbar};

  &.open {
    transform: translateX(0);
  }
`;

const UserMenu = styled.div`
  background-color: ${colors.w01};
  border: 1px solid ${colors.g01};
  cursor: pointer;
  display: ${({ isMenuOpen }) => (isMenuOpen ? 'flex' : 'none')};
  flex-direction: column;
  position: fixed;
  right: 0;
  top: 72px;
  z-index: ${Elevation.Navbar};
  width: 170px;
  border-radius: 4px;
  margin-right: 8px;
  padding: 4px;
`;

const Divider = styled.div`
  border-bottom: 1px solid ${colors.g01};
`;

const StatusWrapper = styled.div`
  display: ${({ show }) => (show ? 'flex' : 'none')};
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  border-bottom: 1px solid ${colors.g01};
`;

const StatusLabel = styled(Typography)`
  text-align: left;
  font-size: 12px;
  color: ${colors.g04};
  font-family: Mada;
`;

const Label = styled(Typography)`
  text-align: left;
  font-size: 16px;
  color: inherit;
  font-family: Mada;
  line-height: 12px;
  border-bottom: ${({ highlightLabel }) => (highlightLabel ? `1px solid ${colors.g04}` : 'none')};
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LabelSmall = styled(Typography)`
  text-align: left;
  font-size: 12px;
  color: ${colors.g04};
  font-family: Mada;
  padding: 11px 0 2px 11px;
`;

const DropdownButton = styled.button.attrs({
  type: 'button'
})`
  align-items: center;
  background-color: transparent;
  border: 0 none;
  cursor: pointer;
  color: ${colors.g04};
  display: ${({ hidden }) => (hidden ? 'none' : 'flex')};
  font-family: Mada;
  font-size: 14px;
  height: 100%;
  padding: 11px 16px 11px 7px;
  position: relative;
  width: 100%;
  border-radius: 4px;
  transition: 0.15s ease;

  &:focus {
    outline: none;
  }

  &[disabled] > ${Label} {
    color: graytext;
  }

  &:hover:not([disabled]) {
    background-color: ${colors.b03};
    color: ${colors.b01};

    .edit-icon {
      background: url(${EditIconBlueImg});
    }
  }
`;

const InnerWrapper = styled.div`
  overflow-y: scroll;
  padding: 40px 16px 16px;
`;

function Layout({
  children,
  dashboardMessage,
  isAvailable,
  isAuthenticated,
  logout,
  hasAgentToken,
  hasActiveCall,
  location,
  history,
  isLoading,
  isProfileIncomplete,
  phoneNumber,
  gotoDashboard
}) {
  const [showEditPhoneNumber, setShowEditPhoneNumber] = useState(false);
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const onLogoutClick = async () => {
    setSidebarOpen(false);
    setMenuOpen(false);
    await rejectReservation(false, true);
    logout();
    destroyWorker();
  };

  const openDropdown = () => {
    if (!hasActiveCall) {
      setMenuOpen(true);
    }
  };

  // eslint-disable-next-line consistent-return
  const onUnavailableClick = async () => {
    const hasReservation = await rejectReservation(true);

    if (!hasReservation) {
      await setUnavailable();
      history.push(ROUTES.HOME);
    }
  };

  const onAvailableClick = async () => {
    if (!isAvailable) {
      await setAvailable();
    }
  };

  const getFormattedPhoneNumber = () => {
    return PhoneNumber(phoneNumber).toJSON().number.international;
  };

  const showAuthenticated = isAuthenticated && hasAgentToken;
  const isShowingGuidelines = location.pathname === ROUTES.GUIDELINES;
  const isInJob = location.pathname.match(/^\/job/);
  const showFaqLink =
    ![ROUTES.LOADING, ROUTES.AGENT_KIT, ROUTES.VENDOR_PRIVACY_TERMS].includes(location.pathname) &&
    !isLoading;
  const isInInstructions = location.pathname === ROUTES.JOB_INSTRUCTIONS;
  const showTopbarNavigation = location.pathname !== ROUTES.VENDOR_PRIVACY_TERMS && !isLoading;
  const formattedPhoneNUmber = phoneNumber ? getFormattedPhoneNumber() : null;

  return (
    <Container>
      <Navbar>
        <Col>
          {showAuthenticated && showTopbarNavigation && (
            <InstructionsButton onClick={() => setSidebarOpen(!isSidebarOpen)}>
              <InstructionsButtonIcon src={InstructionsIcon} />

              <InstructionsButtonText>
                {isSidebarOpen ? 'Hide Guidelines' : 'Show Guidelines'}
              </InstructionsButtonText>
            </InstructionsButton>
          )}
        </Col>

        <Col>
          <LogoNav src={Logo} />
        </Col>

        <Col>
          {showAuthenticated && !isShowingGuidelines && (
            <UserDropdown
              onFocus={() => openDropdown()}
              onBlur={() => setMenuOpen(false)}
              onMouseOver={() => openDropdown()}
              onMouseLeave={() => setMenuOpen(false)}
            >
              <UserIconWrapper>
                <UserIcon src={UserMaleIcon} />

                {isInJob && <StatusIndicator isAvailable={isAvailable} />}
              </UserIconWrapper>

              {!hasActiveCall && <ChevronIcon isMenuOpen={isMenuOpen} src={Chevron} />}

              <UserMenu isMenuOpen={isMenuOpen}>
                {showTopbarNavigation && (
                  <>
                    {formattedPhoneNUmber && (
                      <>
                        <LabelSmall>Your Phone Number</LabelSmall>
                        <DropdownButton onClick={() => setShowEditPhoneNumber(true)}>
                          <Label>
                            {formattedPhoneNUmber}
                            <EditIcon className="edit-icon" />
                          </Label>
                        </DropdownButton>
                      </>
                    )}
                    {!formattedPhoneNUmber && (
                      <>
                        <DropdownButton onClick={() => setShowEditPhoneNumber(true)}>
                          <Label>Add Phone Number</Label>
                        </DropdownButton>
                      </>
                    )}
                    <Divider />
                    <DropdownButton
                      hidden={history.location.pathname === ROUTES.AGENT_KIT}
                      onClick={() => history.push(ROUTES.AGENT_KIT)}
                    >
                      <Label>Agent Kit</Label>
                    </DropdownButton>

                    <DropdownButton
                      hidden={history.location.pathname === ROUTES.HOME}
                      onClick={() => gotoDashboard(history)}
                    >
                      <Label>Dashboard</Label>
                    </DropdownButton>

                    <Divider />

                    <StatusWrapper show={isInJob || isInInstructions}>
                      <StatusLabel
                        padding={{ horizontal: 'small' }}
                        margin={{ top: 'xs', bottom: 'xs' }}
                      >
                        Status
                      </StatusLabel>

                      <DropdownButton disabled={hasActiveCall} onClick={onAvailableClick}>
                        <StatusIndicator isAvailable static />

                        <Label highlightLabel={isAvailable} margin={{ left: 'medium' }}>
                          Available
                        </Label>
                      </DropdownButton>

                      <DropdownButton disabled={hasActiveCall} onClick={onUnavailableClick}>
                        <StatusIndicator static />

                        <Label highlightLabel={!isAvailable} margin={{ left: 'medium' }}>
                          Unavailable
                        </Label>
                      </DropdownButton>
                    </StatusWrapper>
                  </>
                )}

                <DropdownButton disabled={hasActiveCall} onClick={onLogoutClick}>
                  <Label>Log Out</Label>
                </DropdownButton>
              </UserMenu>
            </UserDropdown>
          )}
        </Col>
        {isProfileIncomplete && (
          <NavbarAlert error info fontWeight="700">
            To access the Agent Tool, please complete your Neevo profile{' '}
            <a
              href="https://neevo.definedcrowd.com/en-us/account/login/?reaskRedirRef=AgentsUI"
              target="_blank"
              rel="noopener noreferrer"
            >
              here
            </a>
            .
          </NavbarAlert>
        )}
        {dashboardMessage && (
          <NavbarAlert error info fontWeight="700">
            {dashboardMessage}
          </NavbarAlert>
        )}
      </Navbar>

      <GuidelinesSidebar className={isSidebarOpen && isAuthenticated ? 'open' : ''}>
        <InnerWrapper>
          <GuidelinesText />
        </InnerWrapper>
      </GuidelinesSidebar>

      <Body>{children}</Body>
      {showEditPhoneNumber && (
        <EditPhoneNumberModal
          closeModal={() => setShowEditPhoneNumber(false)}
          phoneNumber={phoneNumber}
        />
      )}
      {showFaqLink && <Faq />}
    </Container>
  );
}

Layout.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.arrayOf(PropTypes.node)
  ]).isRequired,
  dashboardMessage: PropTypes.string,
  hasActiveCall: PropTypes.bool.isRequired,
  hasAgentToken: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func
  }).isRequired,
  logout: PropTypes.func.isRequired,
  gotoDashboard: PropTypes.func.isRequired,
  isAvailable: PropTypes.bool.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isProfileIncomplete: PropTypes.bool.isRequired,
  phoneNumber: PropTypes.string
};

Layout.defaultProps = {
  phoneNumber: null,
  dashboardMessage: null
};

function mapStateToProps({ me, agent, call, app }) {
  return {
    hasActiveCall: !!call.callId,
    hasAgentToken: !!agent.token,
    isAvailable: me.isAvailable,
    isLoading: isLoadingSelector({ agent, me }),
    phoneNumber: getPhoneNumber(me),
    isProfileIncomplete: me.isProfileIncomplete,
    dashboardMessage: app.dashboardMessage
  };
}

function mapDispatchToProps(dispatch) {
  return {
    logout: () => dispatch(logoutAction()),
    gotoDashboard: history => dispatch(gotoDashboardAction(history))
  };
}

const LayoutWithNavigation = withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(Layout)
);

export { LayoutWithNavigation as Layout };
